/**
 * Build AI chat config for academy steps.
 *
 * CourseStart + agent.welcome → first-visit welcome.
 * First AI step after entering academy → resume welcome-back.
 * Later steps in the same visit → enter (step command only, no re-welcome).
 */

import { parseEnvelope, readAiPrompt } from './ai-prompt-envelope';
import { redactString } from './redact-for-log';

export type AiStepKind =
  | 'course-start'
  | 'quiz'
  | 'final-test'
  | 'do-drill'
  | 'gallery'
  | 'read-text'
  | 'clear-word'
  | 'write-essay'
  | 'other';

/** welcome = CourseStart; resume = first step this visit; enter = next steps. */
export type AiEntryReason = 'welcome' | 'resume' | 'enter';

/**
 * How much AI a course runs.
 *
 * `simple` ignores the authored agent and program entirely and speaks one
 * sentence per step; `extended` is the guidance-and-verification behaviour the
 * step programs were written for. Resolved server-side onto the enrollment.
 */
export type CourseAiMode = 'off' | 'simple' | 'extended';

/** Minimal step fields used for AI config + debug dump. */
export interface AiStepRecord {
  id?: number | string | null;
  title?: string | null;
  caption?: string | null;
  actiontype_id?: number | string | null;
  action?: string | null;
  ai?: number | null;
  ai_type?: string | null;
  ai_prompt?: string | null;
  /** Spoken verbatim in simple mode; ignored in extended mode. */
  ai_announce?: string | null;
}

export type AiStepStatus = 'open' | 'pending' | 'done';
export type AiDoneControl = 'done' | 'thumbs';

export interface BuildAiStepChatConfigInput {
  stepKind: AiStepKind;
  stepTitle?: string | null;
  aiType?: string | null;
  aiPrompt?: string | null;
  /** Pre-built content override (glossary JSON, gallery image JSON, …). */
  contentOverride?: string | null;
  /** Raw step row from API — logged for diagnosis. */
  step?: AiStepRecord | null;
  /**
   * From GlobalAiChatService.consumeAcademyStepEntry():
   * resume = first AI step this academy visit; enter = later steps.
   * Ignored for CourseStart welcome.
   */
  sessionEntry?: 'resume' | 'enter';
  /** student_course_step.status_id — drives agent.step-done lines. */
  stepStatusId?: number | string | null;
  /** Done button vs thumbs-up feature. */
  doneControl?: AiDoneControl | null;
  /** Course title for resume welcome-back (“Welcome back to the course …”). */
  courseTitle?: string | null;
}

/** The step the URL is on right now (`step_id=123` or `step_id:123`). */
export function urlStepId(href: string = globalThis.location?.href ?? ''): string | null {
  let url = href;
  try {
    url = decodeURIComponent(href);
  } catch {
    /* malformed escape — match the raw href */
  }
  const match = url.match(/step_id(?:[=:]|%3D|%3A)(\d+)/i);
  return match ? match[1] : null;
}

/** Map academy status_id → step-done coach bucket. */
export function mapAiStepStatus(
  statusId?: number | string | null
): AiStepStatus {
  const n = Number(statusId);
  if (n === 4) return 'done';
  if (n === 2 || n === 3) return 'pending';
  return 'open';
}

export interface BuildAiStepChatConfigResult {
  agentId: string;
  content: string;
  entryReason: AiEntryReason;
}

/**
 * Course AI mode for the enrollment currently open.
 *
 * Module state rather than an input field because every one of the twenty step
 * types calls buildAiStepChatConfig, and the mode is a property of the course
 * they all sit in, not of the step each one is describing. GlobalAiChatService
 * is the only writer: it sets this from the enrollment and clears it on
 * academy exit.
 */
let courseAiMode: CourseAiMode | null = null;

export function setCourseAiMode(mode: CourseAiMode | null): void {
  courseAiMode = mode;
}

/**
 * Where the open step sits in the checksheet (1-based), so the AI can answer
 * "how far am I?" without inventing it. Same single writer as the mode.
 */
let courseStepPosition: { position: number; total: number } | null = null;

export function setCourseStepPosition(position: number | null, total?: number | null): void {
  courseStepPosition =
    position && total && position > 0 && total >= position
      ? { position, total }
      : null;
}

/** `ai_chat_debug`: authored prompts are logged (redacted) only when on. */
let stepAiDebug = false;

export function setStepAiDebug(enabled: boolean): void {
  stepAiDebug = enabled;
}

/** Simple mode says one authored sentence per step and gates nothing. */
const SIMPLE_MODE_AGENT = 'agent.announce';

const CANONICAL_AGENT_BY_KIND: Partial<Record<AiStepKind, string>> = {
  quiz: 'agent.drill',
  'do-drill': 'agent.drill',
  // Page reading: student reads on-screen text; AI verifies then unlocks Done.
  'read-text': 'agent.read-out-loud',
  gallery: 'agent.read-out-loud',
  'clear-word': 'agent.clear-keyword',
  'write-essay': 'agent.write-essay',
};

const DEFAULT_COMMAND_BY_KIND: Partial<Record<AiStepKind, string>> = {
  quiz: 'Complete this quiz drill.',
  'do-drill': 'Complete this drill.',
  'read-text': 'Please read this page out loud.',
  // Gallery welcome / read-out-loud keeps empty default; read-examples uses
  // DEFAULT_COMMAND_BY_AGENT instead of dumping joined slide text.
  gallery: '',
  'clear-word': 'Continue clearing this word.',
  'write-essay': 'Continue with this writing step.',
  other: '',
};

const DEFAULT_COMMAND_BY_AGENT: Record<string, string> = {
  'agent.read-examples':
    'Now please read each of the example sentences out loud.',
  'agent.drill': 'Complete this quiz drill.',
  'agent.write-essay': 'Continue with this writing step.',
};

function stripHtml(html: string): string {
  return (
    String(html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\s+/g, ' ')
      // Trim ONLY inside quotes: `" at "` → `"at"`, keep `word "at" has`.
      .replace(/"([^"]*)"/g, (_m, inner: string) => `"${String(inner).trim()}"`)
      .trim()
  );
}

/** Pull a JSON string field even when the surrounding JSON is broken. */
function extractJsonStringField(
  raw: string,
  field: string
): string | undefined {
  const re = new RegExp(
    `"${field}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`,
    'i'
  );
  const m = raw.match(re);
  if (!m) return undefined;
  try {
    return JSON.parse(`"${m[1]}"`);
  } catch {
    return m[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
}

/**
 * Tolerant recovery when authors leave an unclosed quote in `command`
 * (e.g. `"command": "Click … word at,` missing the closing `"`).
 */
function recoverBrokenPrompt(raw: string): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  const command =
    extractJsonStringField(raw, 'command') ||
    (() => {
      const m = raw.match(
        /"command"\s*:\s*"([^"]*?)(?:"\s*,|"\s*}|$)/i
      );
      return m?.[1]?.trim() || undefined;
    })();
  if (command) out.command = command;

  const content = extractJsonStringField(raw, 'content');
  if (content) out.content = content;

  const definition = extractJsonStringField(raw, 'definition');
  if (definition) out.definition = definition;

  const word = extractJsonStringField(raw, 'word');
  if (word) out.word = word;

  // Recover a sentences array when present as JSON.
  const sentencesMatch = raw.match(/"sentences"\s*:\s*(\[[\s\S]*?\])/i);
  if (sentencesMatch) {
    try {
      const arr = JSON.parse(sentencesMatch[1]);
      if (Array.isArray(arr)) {
        out.sentences = arr.map((s) => String(s)).filter(Boolean);
      }
    } catch {
      /* ignore */
    }
  }

  return Object.keys(out).length ? out : null;
}

function parsePrompt(raw?: string | null): Record<string, unknown> {
  if (!raw || !String(raw).trim()) return {};
  const trimmed = String(raw).trim();
  try {
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ...parsed };
      }
    }
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      return { sentences: JSON.parse(trimmed), content: trimmed };
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[StepAI] ai_prompt.parseFailed', {
      error: String(err),
      length: trimmed.length,
      preview: stepAiDebug ? redactString(trimmed.slice(0, 160)) : undefined,
    });
    const recovered = recoverBrokenPrompt(trimmed);
    if (recovered) {
      // eslint-disable-next-line no-console
      console.warn('[StepAI] ai_prompt.recovered', {
        keys: Object.keys(recovered),
        hasCommand: !!recovered.command,
      });
      return recovered;
    }
  }

  // Starts with `{` but isn't valid/closed JSON — still try recovery.
  if (trimmed.startsWith('{')) {
    // eslint-disable-next-line no-console
    console.warn('[StepAI] ai_prompt.parseFailed', {
      error: 'object-like but not valid JSON',
      length: trimmed.length,
      preview: stepAiDebug ? redactString(trimmed.slice(0, 160)) : undefined,
    });
    const recovered = recoverBrokenPrompt(trimmed);
    if (recovered) {
      // eslint-disable-next-line no-console
      console.warn('[StepAI] ai_prompt.recovered', {
        keys: Object.keys(recovered),
        hasCommand: !!recovered.command,
      });
      return recovered;
    }
  }

  return { content: trimmed };
}

function defaultCommandFor(
  stepKind: AiStepKind,
  agentId?: string | null
): string {
  const byAgent = agentId ? DEFAULT_COMMAND_BY_AGENT[agentId] : '';
  if (byAgent) return byAgent;
  return DEFAULT_COMMAND_BY_KIND[stepKind] || DEFAULT_COMMAND_BY_KIND.other || '';
}

function resolveAgentId(
  stepKind: AiStepKind,
  aiType?: string | null
): { agentId: string; baseEntry: AiEntryReason } {
  const raw = (aiType || '').trim();
  const isWelcome = raw === 'agent.welcome';

  if (stepKind === 'course-start' && (isWelcome || !raw || raw === 'agent.listener')) {
    return {
      agentId: raw && raw !== 'agent.listener' ? raw : 'agent.welcome',
      baseEntry: 'welcome',
    };
  }

  // Authored gallery/welcome steps keep agent.welcome. Resume vs first-visit
  // is controlled by entryReason (welcome-back command), not by remapping to
  // a drill agent like read-out-loud / make-sentences.
  if (isWelcome) {
    return { agentId: 'agent.welcome', baseEntry: 'resume' };
  }

  // Blank / listener → canonical agent for the step kind.
  if (!raw || raw === 'agent.listener') {
    return {
      agentId: CANONICAL_AGENT_BY_KIND[stepKind] || 'agent.drill',
      baseEntry: 'resume',
    };
  }

  if (stepKind === 'quiz' || stepKind === 'do-drill') {
    return { agentId: 'agent.drill', baseEntry: 'resume' };
  }

  return { agentId: raw, baseEntry: 'resume' };
}

/** Detect CourseStart-style first-visit lines so resume/enter do not re-use them. */
function isFirstVisitWelcomeCommand(command: string): boolean {
  const c = (command || '').trim().toLowerCase();
  if (!c || c.startsWith('welcome back')) return false;
  return (
    c.startsWith('hello!') ||
    c.startsWith('hello ') ||
    c.startsWith('welcome!') ||
    c.includes("today we're going to learn") ||
    c.includes('today we are going to learn')
  );
}

/** Titles that are full instructions (not short labels) read poorly in "We are on …". */
function isInstructionalTitle(title: string): boolean {
  const t = (title || '').trim();
  if (!t || t.length > 48) return true;
  if (/[.!?]$/.test(t)) return true;
  const lower = t.toLowerCase();
  return (
    lower.startsWith('here is') ||
    lower.startsWith('here are') ||
    lower.startsWith('please ') ||
    lower.startsWith('read ') ||
    lower.startsWith('complete ')
  );
}

function resolveResumePlace(
  stepTitle: string,
  payload: Record<string, unknown>
): string {
  const word =
    typeof payload.word === 'string' ? stripHtml(payload.word) : '';
  if (word) return `the word "${word}"`;

  const title = stepTitle || 'this step';
  if (isInstructionalTitle(title)) return 'this step';
  if (title.trim().toLowerCase() === 'welcome') return 'this step';
  return title;
}

function sanitizeNextAction(next: string, place: string): string {
  let out = (next || '')
    .replace(/\bgallery step\b/gi, 'step')
    .replace(/\bchecksheet step\b/gi, 'step')
    .trim();
  if (/^continue\.?$/i.test(out)) out = '';
  if (place === 'this step' && /^continue with this step\.?$/i.test(out)) {
    out = '';
  }
  return out;
}

function buildResumeCommand(
  place: string,
  existingCommand: string,
  stepKind: AiStepKind,
  agentId?: string | null,
  courseTitle?: string | null
): string {
  const nextAction = isFirstVisitWelcomeCommand(existingCommand)
    ? ''
    : existingCommand;
  const next = sanitizeNextAction(
    nextAction || defaultCommandFor(stepKind, agentId),
    place
  );
  const course = String(courseTitle || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // Generic "this step" orientation is awkward on Welcome; drop it.
  // Keep "We are on the word …" when place is specific.
  const placeClause =
    place && place.trim().toLowerCase() !== 'this step'
      ? ` We are on ${place} right now.`
      : '';
  const base = course
    ? `Welcome back to the course ${course}.${placeClause}`
    : `Welcome back.${placeClause}`;
  return (next ? `${base} ${next}` : base).replace(/\s+/g, ' ').trim();
}

/**
 * A joined blob is multi-sentence or very long; a one-line instruction is not.
 * Used so short authored commands that happen to equal `content` (quiz drills)
 * are not discarded as sentence dumps.
 */
function contentLooksLikeDump(content: string, sentenceStep: boolean): boolean {
  const t = stripHtml(content);
  if (t.length > 200) return true;
  // "Great! Let's do a short Drill." is two sentences and still one instruction;
  // only a step built from a sentence list joins its captions into content.
  if (!sentenceStep) return false;
  return (t.match(/[.!?](?=\s|$)/g) || []).length > 1;
}

/** Steps whose content is a list of sentences joined together. */
function isSentenceListStep(stepKind: AiStepKind, agentId?: string | null): boolean {
  return agentId === 'agent.read-examples' || stepKind === 'gallery';
}

/**
 * True when command is the joined sentence blob (or equals a dump-like content)
 * — never use that as the spoken instruction for read-examples / gallery.
 *
 * Only equality against dump-like content/sentences counts. Short one-line
 * instructions that equal content (quiz drills) and multi-sentence teacher
 * instructions (e.g. "Here is… Please read… Can you see…?") must stay.
 */
function isJoinedSentencesAsCommand(
  command: string,
  payload: Record<string, unknown>,
  sentenceStep: boolean
): boolean {
  const cmd = spokenKey(command);
  if (!cmd) return false;
  if (
    typeof payload.content === 'string' &&
    spokenKey(payload.content) === cmd &&
    contentLooksLikeDump(payload.content, sentenceStep)
  ) {
    return true;
  }
  if (Array.isArray(payload.sentences) && payload.sentences.length > 1) {
    const joined = spokenKey(
      payload.sentences.map((s) => String(s || '')).join(' ')
    );
    if (joined && joined === cmd) return true;
  }
  return false;
}

/** In-session next step: step command only — never "Welcome back". */
function buildEnterCommand(
  existingCommand: string,
  stepKind: AiStepKind,
  payload: Record<string, unknown>,
  agentId?: string | null
): string {
  // Keep the authored step command, including gallery "Hello! Today…".
  // Stripping first-visit Hellos here caused Welcome slides to fall back to
  // content-as-command, so verbatim spoke the same sentence twice and skipped
  // the real greeting.
  let candidate = existingCommand || defaultCommandFor(stepKind, agentId);
  if (candidate && isJoinedSentencesAsCommand(candidate, payload, isSentenceListStep(stepKind, agentId))) {
    candidate = defaultCommandFor(stepKind, agentId);
  }
  const cmd = sanitizeNextAction(candidate, 'this step');
  if (cmd) return cmd;

  // For read-examples / gallery with sentences: never fall back to joined blob.
  const hasSentences =
    Array.isArray(payload.sentences) && payload.sentences.length > 0;
  if (
    hasSentences &&
    (agentId === 'agent.read-examples' || stepKind === 'gallery')
  ) {
    return defaultCommandFor(stepKind, agentId) ||
      'Now please read each of the example sentences out loud.';
  }

  if (typeof payload.content === 'string' && payload.content.trim()) {
    return stripHtml(payload.content);
  }
  if (typeof payload.definition === 'string' && payload.definition.trim()) {
    return `Read this definition: ${stripHtml(payload.definition)}`;
  }
  return 'Let us continue with this step.';
}

/** Normalize for duplicate detection (command vs content). */
function spokenKey(text: string): string {
  return stripHtml(text)
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function logStepAiSource(
  input: BuildAiStepChatConfigInput,
  resolved: { agentId: string; entryReason: AiEntryReason; builtContent?: string | null }
): void {
  const step = input.step || {};
  const caption =
    (typeof step.caption === 'string' && step.caption.trim()) ||
    (typeof step.title === 'string' && step.title.trim()) ||
    (input.stepTitle || '') ||
    '(no caption)';
  const rawAiPrompt = input.aiPrompt ?? step.ai_prompt ?? null;
  const summary = {
    stepKind: input.stepKind,
    stepType: step.action || input.stepKind,
    actiontype_id: step.actiontype_id ?? null,
    dbAiAgent: input.aiType ?? step.ai_type ?? null,
    resolvedAgentId: resolved.agentId,
    entryReason: resolved.entryReason,
    sessionEntry: input.sessionEntry ?? null,
    aiFlag: step.ai ?? null,
    hasContentOverride: !!input.contentOverride,
    rawAiPromptLength: typeof rawAiPrompt === 'string' ? rawAiPrompt.length : 0,
    builtContentLength: resolved.builtContent?.length ?? 0,
  };

  if (!stepAiDebug) {
    // eslint-disable-next-line no-console
    console.log('[StepAI] step.source', summary);
    return;
  }
  // eslint-disable-next-line no-console
  console.log('[StepAI] step.source', {
    ...summary,
    caption: redactString(caption),
    rawAiPrompt: typeof rawAiPrompt === 'string' ? redactString(rawAiPrompt) : rawAiPrompt,
    builtContent: resolved.builtContent ? redactString(resolved.builtContent) : null,
  });
}

/**
 * Simple mode: one spoken line per step, no program, no verification.
 *
 * The authored agent and the whole `ai_prompt` program are deliberately
 * ignored — a course switched to simple is asking for an announcement, not a
 * cut-down version of a drill. Only the sentence is taken from the authoring,
 * in the order the author is most likely to have meant it: the `simple` half
 * of a combined `ai_prompt`, a purpose-written `ai_announce`, then the
 * command the extended script already opens
 * with, and finally the step title, so a step that was never touched still
 * says something useful.
 *
 * A welcome keeps agent.welcome: it also explains how a simple course works,
 * which the backend's welcome program says after the sentence on a first visit.
 */
function buildSimpleAnnounceConfig(
  input: BuildAiStepChatConfigInput,
  aiType?: string | null,
  aiPrompt?: string | null,
  stepTitle?: string | null,
  simpleCommand?: string | null
): BuildAiStepChatConfigResult | null {
  const step = input.step || {};
  const isWelcome =
    resolveAgentId(input.stepKind, aiType).agentId === 'agent.welcome';

  // The per-step switch still wins: simple is a course-wide style, not an
  // instruction to make every step talk.
  if (Number(step.ai) !== 1) {
    logStepAiSource(
      { ...input, aiPrompt, stepTitle },
      { agentId: 'agent.listener', entryReason: 'enter', builtContent: null }
    );
    return null;
  }

  const promptCommand = parsePrompt(aiPrompt).command;
  const command =
    stripHtml(String(simpleCommand || '')) ||
    stripHtml(String(step.ai_announce || '')) ||
    stripHtml(typeof promptCommand === 'string' ? promptCommand : '') ||
    stripHtml(String(stepTitle || ''));

  // A welcome is never a "welcome back": the backend would rewrite it as one.
  const entryReason: AiEntryReason = isWelcome
    ? 'welcome'
    : input.sessionEntry === 'enter'
      ? 'enter'
      : 'resume';
  const agentId = isWelcome ? 'agent.welcome' : SIMPLE_MODE_AGENT;

  if (!command) {
    logStepAiSource(
      { ...input, aiPrompt, stepTitle },
      { agentId: 'agent.listener', entryReason, builtContent: null }
    );
    return null;
  }

  const content = JSON.stringify({
    command,
    stepKind: input.stepKind,
    entryReason,
    aiMode: 'simple',
    // The welcome's instruction is for a first visit only.
    ...(isWelcome
      ? {
          stepStatus: mapAiStepStatus(input.stepStatusId),
          doneControl: input.doneControl === 'thumbs' ? 'thumbs' : 'done',
        }
      : {}),
  });

  logStepAiSource(
    { ...input, aiPrompt, stepTitle },
    { agentId, entryReason, builtContent: content }
  );

  return { agentId, content, entryReason };
}

/**
 * Returns null when the step should stay on listener (no agent / no content).
 */
export function buildAiStepChatConfig(
  input: BuildAiStepChatConfigInput
): BuildAiStepChatConfigResult | null {
  const stepKind = input.stepKind;
  const aiType = input.aiType ?? input.step?.ai_type;
  const storedPrompt = input.aiPrompt ?? input.step?.ai_prompt;
  const envelope = parseEnvelope(storedPrompt) ? readAiPrompt(storedPrompt) : null;
  const aiPrompt = envelope ? envelope.extended : storedPrompt;
  const stepTitle =
    input.stepTitle ?? input.step?.caption ?? input.step?.title;

  if (courseAiMode === 'simple') {
    return buildSimpleAnnounceConfig(
      input,
      aiType,
      aiPrompt,
      stepTitle,
      envelope?.simple.command
    );
  }

  const { agentId, baseEntry } = resolveAgentId(stepKind, aiType);

  // CourseStart stays welcome; other steps: resume once per visit, then enter.
  let entryReason: AiEntryReason = baseEntry;
  if (baseEntry !== 'welcome') {
    entryReason = input.sessionEntry === 'enter' ? 'enter' : 'resume';
  }

  if (!agentId || agentId === 'agent.listener') {
    logStepAiSource(
      { ...input, aiType, aiPrompt, stepTitle },
      { agentId: agentId || 'agent.listener', entryReason, builtContent: null }
    );
    return null;
  }

  const rawContent = input.contentOverride || aiPrompt;
  if (!rawContent || !String(rawContent).trim()) {
    if (entryReason === 'welcome') {
      logStepAiSource(
        { ...input, aiType, aiPrompt, stepTitle },
        { agentId, entryReason, builtContent: null }
      );
      return null;
    }
  }

  const payload = parsePrompt(rawContent);
  const promptPayload =
    input.contentOverride && aiPrompt ? parsePrompt(aiPrompt) : {};
  const title = stripHtml(stepTitle || '') || 'this step';
  const existingCommand = stripHtml(
    (typeof payload.command === 'string' && payload.command) ||
      (typeof promptPayload.command === 'string' && promptPayload.command) ||
      ''
  );

  if (
    (!payload.content || typeof payload.content !== 'string') &&
    typeof promptPayload.content === 'string'
  ) {
    payload.content = promptPayload.content;
  }
  // Prefer authored definition/word from ai_prompt when override only has slides.
  if (
    (!payload.definition || typeof payload.definition !== 'string') &&
    typeof promptPayload.definition === 'string'
  ) {
    payload.definition = promptPayload.definition;
  }
  if (
    (!payload.word || typeof payload.word !== 'string') &&
    typeof promptPayload.word === 'string'
  ) {
    payload.word = promptPayload.word;
  }
  if (typeof payload.content === 'string') {
    payload.content = stripHtml(payload.content);
  }
  if (typeof payload.definition === 'string') {
    payload.definition = stripHtml(payload.definition);
  }
  if (Array.isArray(payload.sentences) && !payload.content) {
    payload.content = payload.sentences
      .map((s) => stripHtml(String(s || '')))
      .filter(Boolean)
      .join(' ');
  }

  payload.stepTitle = title;
  payload.entryReason = entryReason;
  // Which host step type this is. The agent id alone cannot tell a gallery
  // from a plain read-examples page, and only the gallery is a deck the AI
  // must walk one example at a time.
  payload.stepKind = stepKind;

  // Merge sentences from ai_prompt when override only has content/sentences.
  if (
    !Array.isArray(payload.sentences) &&
    Array.isArray(promptPayload.sentences)
  ) {
    payload.sentences = promptPayload.sentences;
  }

  // When authors only supply definition (glossary shape), mirror into content
  // so verify/readingScore has expected text. Page-read steps should already
  // send full page text via contentOverride.
  if (
    (agentId === 'agent.read-out-loud' || agentId === 'agent.read-definition') &&
    typeof payload.definition === 'string' &&
    payload.definition.trim() &&
    (!payload.content || !String(payload.content).trim())
  ) {
    payload.content = payload.definition;
  }

  // read-examples must not carry a leftover definition (prior step / ai_prompt)
  // or verify/coaching will grade against it instead of sentences.
  if (agentId === 'agent.read-examples' && 'definition' in payload) {
    delete payload.definition;
  }

  // Prefer short authored command; drop joined-blob commands for examples.
  const sentenceStep = isSentenceListStep(stepKind, agentId);
  let commandSeed = existingCommand;
  if (commandSeed && isJoinedSentencesAsCommand(commandSeed, payload, sentenceStep)) {
    commandSeed = '';
  }
  // No extended command: the author's Simple sentence, then (for drills, whose
  // caption is usually the instruction) the step title, before the generic line.
  if (!commandSeed) {
    commandSeed = stripHtml(envelope?.simple.command || '');
  }
  if (!commandSeed && (stepKind === 'quiz' || stepKind === 'do-drill')) {
    commandSeed = stripHtml(stepTitle || '');
  }

  if (entryReason === 'resume') {
    const place = resolveResumePlace(title, payload);
    payload.command = buildResumeCommand(
      place,
      commandSeed,
      stepKind,
      agentId,
      input.courseTitle
    );
  } else if (entryReason === 'enter') {
    // Course-level welcome already covered first-visit hellos — do not
    // re-say "Hello! Today we're going to learn…" on later steps.
    if (commandSeed && isFirstVisitWelcomeCommand(commandSeed)) {
      commandSeed = '';
    }
    payload.command = buildEnterCommand(
      commandSeed,
      stepKind,
      payload,
      agentId
    );
  } else if (!commandSeed && typeof payload.content === 'string') {
    // Welcome: keep content-derived command only when it is not a sentence dump.
    const fromContent = stripHtml(payload.content).slice(0, 280);
    payload.command = isJoinedSentencesAsCommand(fromContent, payload, sentenceStep)
      ? defaultCommandFor(stepKind, agentId) || fromContent
      : fromContent;
  } else if (commandSeed) {
    payload.command = commandSeed;
  }

  // For read-examples: omit giant joined content as speakable definition;
  // sentences[] is the authority.
  if (
    agentId === 'agent.read-examples' &&
    Array.isArray(payload.sentences) &&
    payload.sentences.length
  ) {
    // Keep content for tools that expect it, but never treat it as the command.
    if (
      typeof payload.command === 'string' &&
      isJoinedSentencesAsCommand(payload.command, payload, sentenceStep)
    ) {
      payload.command = defaultCommandFor(stepKind, agentId);
    }
  }

  // Verbatim speaks command + content. If they are the same line, tell the
  // widget to speak once (agents still receive full payload.content).
  if (
    typeof payload.command === 'string' &&
    typeof payload.content === 'string' &&
    spokenKey(payload.command) &&
    spokenKey(payload.command) === spokenKey(payload.content)
  ) {
    payload.skipDuplicateContentSpeak = true;
  }

  // Host context for agent.step-done / stepDone.auto scripts.
  // Keep stepStatus for UI (Done/thumbs) but do NOT rewrite the command —
  // revisiting a completed step uses the normal step instructions again.
  payload.stepStatus = mapAiStepStatus(input.stepStatusId);
  payload.doneControl =
    input.doneControl === 'thumbs' ? 'thumbs' : 'done';

  // Course-level facts for help answers ("what is this course for?", "how far
  // am I?"). Purpose and the outline come from the enrollment-scoped
  // ai-context endpoint the widget reads itself.
  const courseTitle = stripHtml(String(input.courseTitle || ''));
  if (courseTitle) payload.courseTitle = courseTitle;
  if (courseStepPosition) {
    payload.stepPosition = courseStepPosition.position;
    payload.stepTotal = courseStepPosition.total;
  }

  const builtContent = JSON.stringify(payload);
  logStepAiSource(
    { ...input, aiType, aiPrompt, stepTitle: title },
    { agentId, entryReason, builtContent }
  );

  return {
    agentId,
    content: builtContent,
    entryReason,
  };
}
